import React, { useEffect, useState } from 'react';
import styles from './RelatedRequestPreview.module.scss';
import cn from 'classnames';
import { Icon, Spin, Tooltip } from 'antd';
import { getBlobUrl } from '../../../../../../../helpers/getBlobUrl';
import useAttachmentUrl from '../../../../../queries/useAttachmentUrl';
import { Lightbox } from '../../../../../../../modal_image';

const Item = ({ attachment, requestId, onPreview }) => {
  const fileExtension = attachment.fileName?.split('.').pop().toLowerCase();

  const isPicture = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(
    fileExtension
  );

  const { data, isFetching } = useAttachmentUrl(
    {
      requestId,
      fileName: attachment.fileName,
    },
    {
      enabled: isPicture,
    }
  );

  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!data || !isPicture) return;

    const url = getBlobUrl(data, fileExtension);
    setBlobUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [data, fileExtension, isPicture]);

  return (
    <Tooltip title={attachment?.name || ''}>
      <div
        className={cn(styles.item, {
          [styles.picture]: isPicture,
        })}
        style={{ cursor: isPicture ? 'pointer' : 'auto' }}
      >
        {isFetching ? (
          <Spin />
        ) : isPicture ? (
          <img
            src={blobUrl}
            alt={attachment.fileName || ''}
            draggable={false}
            className={styles.imgCustom}
            onClick={() => {
              if (blobUrl && isPicture) {
                onPreview(blobUrl, attachment.fileName);
              }
            }}
          />
        ) : (
          <Icon
            type="file"
            style={{ color: '#0995ED', fontSize: 24 }}
          />
        )}
      </div>
    </Tooltip>
  );
};

const RelatedRequestPreview = ({ attachments = [], requestId }) => {
  const [showButtons, setShowButtons] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(null);

  const scrollRef = React.useRef();

  const handlePreview = (url, alt) => {
    setLightboxOpen({ url, alt });
  };

  const scrollNext = () => {
    scrollRef.current.scrollBy({ left: 120, behavior: 'smooth' });
  };

  const scrollPrev = () => {
    scrollRef.current.scrollBy({ left: -120, behavior: 'smooth' });
  };

  const updateButtonsVisibility = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowButtons(el.scrollWidth > el.clientWidth);
  };

  useEffect(() => {
    updateButtonsVisibility();
    window.addEventListener('resize', updateButtonsVisibility);
    return () => window.removeEventListener('resize', updateButtonsVisibility);
  }, [attachments]);

  return (
    <>
      <div className={styles.container}>
        {showButtons && (
          <Icon
            type="left"
            onClick={scrollPrev}
            style={{ color: '#0995ED' }}
          />
        )}
        <div
          className={styles.root}
          ref={scrollRef}
        >
          {attachments?.map((attachment) => (
            <Item
              key={attachment.id}
              attachment={attachment}
              requestId={requestId}
              onPreview={handlePreview}
            />
          ))}
        </div>
        {showButtons && (
          <Icon
            type="right"
            onClick={scrollNext}
            style={{ color: '#0995ED' }}
          />
        )}
      </div>
      {lightboxOpen && (
        <Lightbox
          medium={lightboxOpen?.url || null}
          large={lightboxOpen?.url || null}
          alt={lightboxOpen?.alt || ''}
          showRotate={true}
          onClose={() => setLightboxOpen(null)}
        />
      )}
    </>
  );
};

export default RelatedRequestPreview;
